/* eslint-disable @typescript-eslint/naming-convention */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import type {
    AddOrderPayload,
    AddTicketPayload,
    AddTicketResponsePayload,
    GetPaymentOrdersOptions,
    OfficeLoginPayload,
    OfficeLoginRefreshPayload,
    UpdateOrderStatusPayload
} from '@shared/interfaces/backoffice';

import Config from '@config/config';
import { OfficeAuthToken } from '@services/auth-token/officeAuthToken';
import { queryBuilder } from '@services/url/url';

import { BillingDataPayload } from '@shared/interfaces/backoffice/billingData';
import { BaseClient } from './_baseClient';

const singleton         = Symbol();
const singletonEnforcer = Symbol();
const { api }           = Config;

class CBackOfficeAPIClient extends BaseClient {

    private constructor(enforcer) {
        super();

        try {
            if (enforcer !== singletonEnforcer) {
                throw new Error('Cannot construct singleton');
            }

            this._baseURL = api.backofficeApiDomain;
            this._config  = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${OfficeAuthToken.getToken()}`,
                }
            };
            this._session = axios.create({ withCredentials: false, baseURL: this._baseURL });
        } catch (e: unknown) {
            console.error(e);
        }
    }

    initConfig() {
        this._initSession();
    }

    protected _initSession() {
        this._baseURL = api.backofficeApiDomain;
        this._config  = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: OfficeAuthToken.getToken() ? `Bearer ${OfficeAuthToken.getToken()}` : '',
            }
        };
        this._session = axios.create({ withCredentials: false, baseURL: this._baseURL });

        this._session.interceptors.response.use(
            (response) => response,
            (error: { response: Record<string, any> }) => {
                if (axios.isCancel(error)) {
                    console.log('[interceptors.response]: request canceled');

                    return;
                }

                return Promise.reject(error);
            }
        );
    }

    static get instance(): CBackOfficeAPIClient & AxiosInstance {
        // Try to get an efficient singleton
        if (!this[singleton]) {
            this[singleton] = new CBackOfficeAPIClient(singletonEnforcer);
        }

        return this[singleton];
    }





    getPlans<AxiosReturn = any>(config?: AxiosRequestConfig) {
        return this.get<AxiosReturn>('/publicapi/plans', { ...this._config, ...config });
    }
    // login<AxiosReturn = OfficeLoginResponse>(data: OfficeLoginPayload, config?: AxiosRequestConfig) {
    //     return this.post<AxiosReturn>(`${api.backofficeApiDomain}/api/login`, data, { ...this._config, ...config, withCredentials: false });
    // }
    async login(data: OfficeLoginPayload, config?: AxiosRequestConfig) {
        return fetch(`${api.backofficeApiDomain}/api/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data),
            ...config,
        });
    }
    refreshToken<AxiosReturn = any>(data: OfficeLoginRefreshPayload, config?: AxiosRequestConfig) {
        return this.post<AxiosReturn>(`${api.backofficeApiDomain}/api/token/refresh`, data, { ...this._config, ...config, withCredentials: false });
    }
    protected handleRefreshCheck() {
        const token        = OfficeAuthToken.getToken();
        const tokenHandler = new OfficeAuthToken(token);
        const refreshToken = OfficeAuthToken.getRefreshToken();

        if (!tokenHandler.isExpired && tokenHandler.isValid) {
            // logout here or show error
            return;
        }

        if (!refreshToken) {
            return;
        }

        return (
            this.refreshToken({
                refresh_token: refreshToken,
            })
                .then((response) => {
                    if (response.status === 401) {
                        window.location.reload();
                    }

                    if (!response.data) {
                        throw new Error('Failed to refresh token');
                    }

                    OfficeAuthToken.storeToken(response.data.token, response.data.refresh_token);
                    this._config  = {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: OfficeAuthToken.getToken() ? `Bearer ${OfficeAuthToken.getToken()}` : '',
                        }
                    };
                    this._session = axios.create({ withCredentials: false, baseURL: this._baseURL });
                })
                .catch((error) => {
                    console.log('[interceptors.response]: refresh token failed');

                    const message = String(error?.message || error);

                    console.log(message);

                    if (message.includes('401')) {
                        OfficeAuthToken.removeToken();
                        window.location.reload();
                    }
                })
        );
    }






    async getActiveOrder<AxiosReturn = any>(clientId: string, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        return this.get<AxiosReturn>(`/api/v1/client/${clientId}/active-order`, { ...this._config, ...config });
    }
    async getOrders<AxiosReturn = any>(clientId: string, options: GetPaymentOrdersOptions ,config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        const id  = clientId ?? 'inkyfada';
        const url = queryBuilder(`/api/v1/client/${id}/orders`);

        return this.get<AxiosReturn>(url, {
            ...this._config,
            ...config,
            params: {
                ...config.params,
                ...options,
            }
        });
    }
    async addOrUpdateOrder<AxiosReturn = any>(data: AddOrderPayload, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        return this.post<AxiosReturn>('/api/v1/checkout', data, { ...this._config, ...config });
    }
    async updateOrderStatus<AxiosReturn = any>(orderId: string, data: UpdateOrderStatusPayload, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        return this.post<AxiosReturn>(`/api/v1/order/${orderId}/status`, data, { ...this._config, ...config });
    }

    async downloadInvoice<AxiosReturn = any>(orderReference: string, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        return this.get<AxiosReturn>(`/api/v1/order/${orderReference}/invoice`, {
            ...this._config,
            ...config,
            responseType: 'blob',
            headers: {
                ...this._config.headers,
                ...this._config.headers,
                Accept: 'application/pdf',
            }
        });
    }

    async upgradePlan<AxiosReturn = any>(clientId: string, newPlanId: number, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        return this.post<AxiosReturn>(`/api/v1/client/${clientId}/plan-upgrade`, { newPlanId }, { ...this._config, ...config });
    }


    async getBillingData<AxiosReturn = BillingDataPayload>(id: string, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        const url = queryBuilder(`/api/v1/client/${id}/billing`);

        return this.get<AxiosReturn>(url, { ...this._config, ...config });
    }
    async updateBillingData<AxiosReturn = any>(data: BillingDataPayload, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        return this.post<AxiosReturn>(`/api/v1/client/${data.clientId}/billing`, data, { ...this._config, ...config });
    }




    async addTicket<AxiosReturn = any>(data: AddTicketPayload, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        return this.post<AxiosReturn>('/api/ticket/create', data, { ...this._config, ...config });
    }
    async getTicketDetails<AxiosReturn = any>(id: string, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        const url = queryBuilder(`/api/ticket/${id}`);

        return this.get<AxiosReturn>(url, { ...this._config, ...config });
    }
    async getClientTickets<AxiosReturn = any>(externalId: string, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        const url = queryBuilder(`/api/ticket/client/${externalId}`);

        return this.get<AxiosReturn>(url, { ...this._config, ...config });
    }
    async addTicketResponse<AxiosReturn = any>(id: string, data: AddTicketResponsePayload, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        return this.post<AxiosReturn>(`/api/ticket/${id}/response`, data, { ...this._config, ...config });
    }
}

export const BackOfficeAPIClient = CBackOfficeAPIClient.instance;
